import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import AutoDevisStaffBotChat from '../components/AutoDevisStaffBotChat';
import { isMagasinAdmin } from '../utils/roleRouting';
import {
  Bot, CheckCircle2, XCircle, Eye, RefreshCw, FileText, MessageSquare, Layers,
  Trash2, Info, ChevronDown, ChevronUp, Store, Printer, Mail, User, Phone, Save, AlertTriangle,
} from 'lucide-react';
import { printTicket80mm } from '../utils/ticket80mmPrint';

// Fiche client obligatoire avant validation (règle direction) — miroir de services/chat_identity_gate.py
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const GENERIC_NAMES = new Set(['', 'client', 'client comptoir', 'client divers', 'divers', 'comptoir', 'visiteur vitrine', 'visiteur', 'invité', 'guest', 'passage']);
export function identityMissing(d) {
  const payload = d?.calcul_payload || {};
  const name = String(d?.client_name || payload.client_nom || '').trim();
  const tel = String(d?.client_tel || payload.client_tel || '').replace(/\D/g, '');
  const email = String(d?.client_email || payload.client_email || '').trim();
  const missing = [];
  if (name.length < 2 || GENERIC_NAMES.has(name.toLowerCase())) missing.push('nom');
  if (tel.length < 9 || tel.length > 15) missing.push('téléphone');
  if (!EMAIL_RE.test(email)) missing.push('e-mail');
  return missing;
}

// Valeurs gamme backend (brutes) -> clés i18n (affichage uniquement ; repli sur la valeur brute).
const QUALITY_LABEL_KEYS = {
  incell: 'chatAiDevis.qualityIncell',
  oled: 'chatAiDevis.qualityOled',
  original: 'chatAiDevis.qualityOriginal',
  compatible: 'chatAiDevis.qualityCompatible',
  reconditionné: 'chatAiDevis.qualityReconditionné',
  premium: 'chatAiDevis.qualityPremium',
  standard: 'chatAiDevis.qualityStandard',
};

// Champs manquants renvoyés (bruts) par identityMissing() -> clés i18n (affichage uniquement).
const MISSING_FIELD_KEYS = {
  nom: 'chatAiDevis.fieldName',
  'téléphone': 'chatAiDevis.fieldPhone',
  'e-mail': 'chatAiDevis.fieldEmail',
};

const TIER_ORDER = ['incell', 'oled', 'reconditionné', 'original', 'compatible', 'premium', 'standard'];

const sortByTier = (a, b) => {
  const qa = TIER_ORDER.indexOf(a.intent?.quality || 'standard');
  const qb = TIER_ORDER.indexOf(b.intent?.quality || 'standard');
  return (qa === -1 ? 99 : qa) - (qb === -1 ? 99 : qb);
};

const DevisAmountInput = ({
  value,
  onChange,
  disabled = false,
  step = '0.01',
  id,
}) => (
  <div className="relative w-full">
    <input
      id={id}
      type="number"
      step={step}
      min="0"
      disabled={disabled}
      value={value}
      onChange={onChange}
      className="w-full h-11 pl-3.5 pr-10 rounded-lg bg-[#0f0f14] border border-white/25 text-white text-base font-semibold tabular-nums shadow-[inset_0_1px_2px_rgba(0,0,0,0.45)] focus:outline-none focus:border-cyan-400/80 focus:ring-2 focus:ring-cyan-500/30 disabled:opacity-50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />
    <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">
      €
    </span>
  </div>
);

const groupDevis = (items) => {
  const batches = new Map();
  const singles = [];
  for (const d of items) {
    if (d.batch_id) {
      if (!batches.has(d.batch_id)) batches.set(d.batch_id, []);
      batches.get(d.batch_id).push(d);
    } else {
      singles.push(d);
    }
  }
  for (const [, list] of batches) list.sort(sortByTier);
  return { batches: [...batches.entries()], singles };
};

const supplierBadgeClass = (supplier) => {
  if (supplier === 'JensMobiles') return 'bg-emerald-500/20 text-emerald-300';
  if (supplier === 'Mobilax') return 'bg-blue-500/20 text-blue-300';
  if (supplier === 'LCD-Phone') return 'bg-violet-500/20 text-violet-300';
  return 'bg-white/10 text-gray-300';
};

const familyBadge = (intent = {}) => {
  const f = intent.family || '';
  if (f === 'accessory' || intent.from_stock || intent.on_order) {
    if (intent.from_stock) return { labelKey: 'chatAiDevis.familyAccessoryStock', cls: 'bg-green-500/20 text-green-300' };
    if (intent.price_capped) return { labelKey: 'chatAiDevis.familyAccessoryCapped', cls: 'bg-orange-500/20 text-orange-300' };
    return { labelKey: 'chatAiDevis.familyAccessory', cls: 'bg-pink-500/20 text-pink-300' };
  }
  if (f === 'repair_part') return { labelKey: 'chatAiDevis.familyPart', cls: 'bg-cyan-500/20 text-cyan-300' };
  if (f === 'screen' || intent.part === 'écran') return { labelKey: 'chatAiDevis.familyScreen', cls: 'bg-amber-500/20 text-amber-300' };
  return null;
};

// CHAT-W7 — badges de fiabilité du snapshot fournisseur (fallback / score / à vérifier)
const snapshotBadges = (snap = {}) => {
  const out = [];
  if (snap.is_fallback) out.push({ labelKey: 'chatAiDevis.badgeFallback', cls: 'bg-orange-500/20 text-orange-300' });
  const score = snap.match_score_raw ?? snap.match_score;
  if (score !== undefined && score !== null && score !== '' && Number.isFinite(Number(score))) {
    out.push({ labelKey: 'chatAiDevis.badgeModelScore', vars: { score: Math.round(Number(score)) }, cls: 'bg-sky-500/20 text-sky-300' });
  }
  if (snap.needs_review) out.push({ labelKey: 'chatAiDevis.badgeNeedsReview', cls: 'bg-red-500/20 text-red-300' });
  return out;
};

// CHAT-W5 — comparatif multi-fournisseurs (GET /chat-ai-devis/{id}/compare)
const COMPARE_SUPPLIERS = ['JensMobiles', 'Mobilax', 'LCD-Phone'];
const priceNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : Infinity;
};
const candKey = (c) => `${c?.supplier || ''}|${c?.supplier_id || c?.reference || ''}`;
const flattenCompare = (data) => {
  const by = data?.by_supplier || {};
  const suppliers = [...COMPARE_SUPPLIERS, ...Object.keys(by).filter((x) => !COMPARE_SUPPLIERS.includes(x))];
  const rows = [];
  suppliers.forEach((sup) => (by[sup] || []).forEach((c) => rows.push({ ...c, supplier: c.supplier || sup })));
  rows.sort((a, b) => priceNum(a.price) - priceNum(b.price));
  return rows;
};

const ChatAiDevisPage = () => {
  const { api, user } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const [searchParams] = useSearchParams();
  const canManageLots = isMagasinAdmin(user); // accepter/éliminer les lots (direction + admin magasin)
  // Traduit les champs bruts renvoyés par identityMissing() ('nom', 'téléphone', 'e-mail') à l'affichage.
  const tMissing = (arr) => (arr || []).map((x) => t(MISSING_FIELD_KEYS[x] || x)).join(', ');
  // Libellé de gamme : valeur backend brute -> i18n, repli sur la valeur brute puis 'Standard'.
  const qualityLabel = (q) => ((q && QUALITY_LABEL_KEYS[q]) ? t(QUALITY_LABEL_KEYS[q]) : (q || t('chatAiDevis.qualityStandard')));
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);
  const [editMo, setEditMo] = useState({});
  const [editAchat, setEditAchat] = useState({});
  const [editIdent, setEditIdent] = useState({});
  const [alternatives, setAlternatives] = useState({});
  const [expandedAlts, setExpandedAlts] = useState({});
  const [compare, setCompare] = useState({});
  const [expandedCompare, setExpandedCompare] = useState({});
  const [botMode, setBotMode] = useState('workspace');
  const [botChat, setBotChat] = useState(null);
  const [showMobileBot, setShowMobileBot] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/chat-ai-devis?status=${filter}`);
      setDevis(res.data.devis || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => { load(); }, [load]);

  const { batches, singles } = useMemo(() => groupDevis(devis), [devis]);

  const openBotChat = useCallback((meta) => {
    if (!meta?.conversationId) return;
    setBotMode('client');
    setBotChat(meta);
    setShowMobileBot(true);
  }, []);

  const resetBotWorkspace = useCallback(() => {
    setBotMode('workspace');
    setBotChat(null);
  }, []);

  useEffect(() => {
    const convParam = searchParams.get('conversation');
    if (!convParam || devis.length === 0) return;
    const match = devis.find((d) => d.conversation_id === convParam);
    if (match) {
      setBotMode('client');
      openBotChat({
        conversationId: match.conversation_id,
        clientName: match.client_name,
        clientMessage: match.client_message,
        clientTel: match.client_tel,
        clientEmail: match.client_email || match.calcul_payload?.client_email || '',
      });
    }
  }, [devis, searchParams, openBotChat]);

  const previewPdf = async (id) => {
    try {
      const res = await api.get(`/chat-ai-devis/${id}/pdf-preview`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      alert(t('chatAiDevis.errPdfPreview'));
    }
  };

  const printDevisTicket = async (d) => {
    const payload = d.calcul_payload || {};
    const item = (payload.items || [])[0] || {};
    const snap = payload.supplier_snapshot || {};
    const achatVal = editAchat[d.id] ?? item.prix_achat ?? snap.price ?? 0;
    const moVal = editMo[d.id] ?? payload.main_oeuvre ?? 50;
    if (Number(achatVal) <= 0) {
      alert(t('chatAiDevis.alertPurchaseMissingPrint'));
      return;
    }
    try {
      await printTicket80mm(api, {
        type: 'calcul',
        payload: {
          items: [{
            description: item.description || snap.part_name || `${(d.intent || {}).part || t('chatAiDevis.defaultPart')} ${(d.intent || {}).model || ''}`.trim(),
            prix_achat: parseFloat(achatVal) || 0,
            reference: item.reference || payload.ref_piece || snap.reference || '',
          }],
          quantite: payload.quantite || 1,
          main_oeuvre: parseFloat(moVal) || 0,
          client_nom: d.client_name || '',
          client_tel: d.client_tel || '',
          client_email: d.client_email || payload.client_email || '',
          ref_piece: payload.ref_piece || item.reference || snap.reference || '',
          fournisseur: payload.fournisseur || snap.supplier || 'JensMobiles',
          doc_type: 'DEVIS',
          save_client: false,
          accept_online_payment: false,
        },
      });
    } catch (e) {
      alert(e.response?.data?.detail || t('chatAiDevis.errPrintTicket'));
    }
  };

  const patchMo = async (id) => {
    const mo = parseFloat(editMo[id]);
    const achat = editAchat[id] != null ? parseFloat(editAchat[id]) : undefined;
    if (Number.isNaN(mo)) return;
    setBusyId(id);
    try {
      const body = { main_oeuvre: mo };
      if (achat != null && !Number.isNaN(achat)) body.prix_achat = achat;
      await api.patch(`/chat-ai-devis/${id}`, body);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || t('chatAiDevis.errRecalc'));
    } finally {
      setBusyId(null);
    }
  };

  const refreshPart = async (id, opts = {}) => {
    setBusyId(id);
    try {
      await api.post(`/chat-ai-devis/${id}/refresh-part`, opts);
      await load();
      setAlternatives((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setCompare((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (e) {
      alert(e.response?.data?.detail || t('chatAiDevis.errPartNotFound'));
    } finally {
      setBusyId(null);
    }
  };

  // CHAT-W5 — tableau Jens / Mobilax / LCD-Phone pour un brouillon
  const loadCompare = async (id) => {
    if (compare[id]) {
      setExpandedCompare((prev) => ({ ...prev, [id]: !prev[id] }));
      return;
    }
    setBusyId(id);
    try {
      const res = await api.get(`/chat-ai-devis/${id}/compare`);
      setCompare((prev) => ({ ...prev, [id]: res.data }));
      setExpandedCompare((prev) => ({ ...prev, [id]: true }));
    } catch (e) {
      alert(e.response?.data?.detail || t('chatAiDevis.errCompare'));
    } finally {
      setBusyId(null);
    }
  };

  const loadAlternatives = async (id) => {
    if (alternatives[id]) {
      setExpandedAlts((prev) => ({ ...prev, [id]: !prev[id] }));
      return;
    }
    setBusyId(id);
    try {
      const res = await api.get(`/chat-ai-devis/${id}/alternatives`);
      setAlternatives((prev) => ({ ...prev, [id]: res.data }));
      setExpandedAlts((prev) => ({ ...prev, [id]: true }));
    } catch (e) {
      alert(e.response?.data?.detail || t('chatAiDevis.errAlternatives'));
    } finally {
      setBusyId(null);
    }
  };

  const saveIdentity = async (d) => {
    const cur = editIdent[d.id] || {};
    const body = {
      client_name: (cur.name ?? (d.client_name || '')).trim(),
      client_tel: (cur.tel ?? (d.client_tel || '')).trim(),
      client_email: (cur.email ?? (d.client_email || d.calcul_payload?.client_email || '')).trim(),
    };
    setBusyId(d.id);
    try {
      await api.patch(`/chat-ai-devis/${d.id}`, body);
      setEditIdent((prev) => { const n = { ...prev }; delete n[d.id]; return n; });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || t('chatAiDevis.errSaveIdentity'));
    } finally {
      setBusyId(null);
    }
  };

  const approve = async (id) => {
    const d = devis.find((x) => x.id === id);
    const missingId = identityMissing(d);
    if (missingId.length) {
      alert(t('chatAiDevis.alertIdentityIncomplete', { fields: tMissing(missingId) }));
      return;
    }
    const email = d?.client_email || d?.calcul_payload?.client_email || '';
    const mailHint = email
      ? `\n\n${t('chatAiDevis.mailWillSend', { email })}`
      : `\n\n${t('chatAiDevis.noClientEmail')}`;
    const mo = editMo[id] != null ? parseFloat(editMo[id]) : undefined;
    if (!window.confirm(`${t('chatAiDevis.confirmApprove')}${mailHint}`)) return;
    setBusyId(id);
    try {
      const res = await api.post(`/chat-ai-devis/${id}/approve`, {
        main_oeuvre: Number.isNaN(mo) ? undefined : mo,
      });
      await load();
      const r = res?.data || {};
      const sumupWarn = r.sumup_acompte_ok === false
        ? `\n\n${t('chatAiDevis.sumupWarn', { error: r.sumup_acompte_error || t('chatAiDevis.sumupUnavailable') })}`
        : '';
      alert((email ? t('chatAiDevis.approvedMailSent', { email }) : t('chatAiDevis.approvedSent')) + sumupWarn);
    } catch (e) {
      alert(e.response?.data?.detail || t('chatAiDevis.errApprove'));
    } finally {
      setBusyId(null);
    }
  };

  const approveBatch = async (batchId, count) => {
    if (!window.confirm(t('chatAiDevis.confirmApproveBatch', { count }))) return;
    setBusyId(batchId);
    try {
      await api.post(`/chat-ai-devis/batch/${batchId}/approve`, {});
      await load();
      alert(t('chatAiDevis.batchApproved', { count }));
    } catch (e) {
      alert(e.response?.data?.detail || t('chatAiDevis.errApproveBatch'));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt(t('chatAiDevis.promptRejectReason')) || '';
    setBusyId(id);
    try {
      await api.post(`/chat-ai-devis/${id}/reject`, { reason });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || t('chatAiDevis.errGeneric'));
    } finally {
      setBusyId(null);
    }
  };

  const deleteDevis = async (id, ref) => {
    if (!window.confirm(t('chatAiDevis.confirmDeleteDevis', { ref: ref || id }))) return;
    setBusyId(id);
    try {
      await api.delete(`/chat-ai-devis/${id}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || t('chatAiDevis.errDelete'));
    } finally {
      setBusyId(null);
    }
  };

  const deleteBatch = async (batchId, count) => {
    if (!window.confirm(t('chatAiDevis.confirmDeleteBatch', { count }))) return;
    setBusyId(batchId);
    try {
      await api.delete(`/chat-ai-devis/batch/${batchId}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || t('chatAiDevis.errDeleteBatch'));
    } finally {
      setBusyId(null);
    }
  };

  const renderAlternatives = (d) => {
    const data = alternatives[d.id];
    if (!expandedAlts[d.id] || !data?.alternatives?.length) return null;
    const currentRef = data.current_reference;
    const currentSupplier = d.calcul_payload?.fournisseur;
    return (
      <div className="rounded-xl border border-white/15 bg-black/50 p-3 space-y-2">
        <p className="text-[11px] text-gray-400 uppercase tracking-wide font-semibold">
          {t('chatAiDevis.altOtherParts')}
        </p>
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          {data.alternatives.map((alt) => {
            const isCurrent = alt.reference === currentRef && alt.supplier === currentSupplier;
            return (
              <div
                key={`${alt.supplier}-${alt.reference}`}
                className={`rounded-lg border p-3 space-y-2 ${
                  isCurrent
                    ? 'border-emerald-500/40 bg-emerald-500/10'
                    : 'border-white/10 bg-white/[0.03]'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${supplierBadgeClass(alt.supplier)}`}>
                      {alt.supplier}{alt.is_primary_supplier ? ' ★' : ''}
                    </span>
                    {alt.reference && (
                      <span className="text-[10px] font-mono text-gray-400 shrink-0">{alt.reference}</span>
                    )}
                    {isCurrent && (
                      <span className="text-[10px] text-emerald-400 font-medium shrink-0">{t('chatAiDevis.altSelected')}</span>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className="text-sm font-bold text-emerald-400 tabular-nums">
                      {formatCurrency(Number(alt.price || 0))}
                    </span>
                    {!isCurrent && (
                      <button
                        type="button"
                        disabled={busyId === d.id || d.status !== 'pending'}
                        onClick={() => refreshPart(d.id, { supplier: alt.supplier, reference: alt.reference })}
                        className="text-[11px] px-2.5 py-1 rounded-md bg-cyan-600/20 text-cyan-300 hover:bg-cyan-600/35 disabled:opacity-40"
                      >
                        {t('chatAiDevis.useBtn')}
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-100 leading-relaxed break-words whitespace-normal">
                  {alt.part_name || '—'}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // CHAT-W5 — tableau de comparaison multi-fournisseurs (tri par prix, badge « moins cher »)
  const renderCompare = (d) => {
    const data = compare[d.id];
    if (!expandedCompare[d.id] || !data) return null;
    const rows = flattenCompare(data);
    const recKey = data.recommended ? candKey(data.recommended) : null;
    const cur = data.current || {};
    const curKey = cur.supplier ? `${cur.supplier}|${cur.supplier_id || cur.reference || ''}` : null;
    const missing = COMPARE_SUPPLIERS.filter((sup) => !((data.by_supplier || {})[sup] || []).length);
    return (
      <div className="rounded-xl border border-white/15 bg-black/50 p-3 space-y-2" data-testid={`compare-${d.id}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide font-semibold">
            {t('chatAiDevis.compareTitle')}{data.tier ? ` · ${qualityLabel(data.tier)}` : ''}
          </p>
          {cur.supplier && (
            <span className="text-[10px] text-gray-500">
              {t('chatAiDevis.compareCurrentLabel')} {cur.supplier} · {formatCurrency(Number(cur.price || 0))} {t('chatAiDevis.htSuffix')}
            </span>
          )}
        </div>
        {rows.length === 0 ? (
          <p className="text-xs text-gray-500">{t('chatAiDevis.compareNoneFound')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-gray-500 border-b border-white/10">
                  <th className="text-left py-1.5 pr-2 font-semibold">{t('chatAiDevis.colSupplier')}</th>
                  <th className="text-left py-1.5 pr-2 font-semibold">{t('chatAiDevis.colPart')}</th>
                  <th className="text-right py-1.5 pr-2 font-semibold">{t('chatAiDevis.colPurchaseHt')}</th>
                  <th className="text-right py-1.5 pr-2 font-semibold">{t('chatAiDevis.colTtcEst')}</th>
                  <th className="text-center py-1.5 pr-2 font-semibold">{t('chatAiDevis.colStock')}</th>
                  <th className="text-center py-1.5 pr-2 font-semibold">{t('chatAiDevis.colExactModel')}</th>
                  <th className="py-1.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const key = candKey(c);
                  const isRec = Boolean(recKey) && key === recKey;
                  const isCur = Boolean(curKey) && key === curKey;
                  const score = c.match_score_raw ?? c.match_score;
                  return (
                    <tr key={`${key}-${c.reference || ''}`} className={`border-b border-white/5 ${isRec ? 'bg-emerald-500/10' : ''}`}>
                      <td className="py-1.5 pr-2 align-top">
                        <div className="flex flex-wrap items-center gap-1">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${supplierBadgeClass(c.supplier)}`}>
                            {c.supplier}
                          </span>
                          {isRec && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/25 text-emerald-300 font-semibold">
                              {t('chatAiDevis.badgeCheapest')}
                            </span>
                          )}
                          {isCur && <span className="text-[10px] text-emerald-400 font-medium">{t('chatAiDevis.badgeCurrent')}</span>}
                          {c.is_fallback && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-300">
                              {t('chatAiDevis.badgeFallback')}
                            </span>
                          )}
                        </div>
                        {c.reference && <div className="text-[10px] font-mono text-gray-500 mt-0.5">{c.reference}</div>}
                      </td>
                      <td className="py-1.5 pr-2 align-top text-gray-100 min-w-[180px] max-w-[320px] break-words">
                        {c.part_name || '—'}
                        {score !== undefined && score !== null && score !== '' && (
                          <span className="ml-1 text-[10px] text-sky-300">· {t('chatAiDevis.scoreInline', { score: Math.round(Number(score)) })}</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-2 align-top text-right font-semibold text-emerald-400 tabular-nums">
                        {formatCurrency(Number(c.price || 0))}
                      </td>
                      <td className="py-1.5 pr-2 align-top text-right text-gray-200 tabular-nums">
                        {c.price_ttc_estime !== undefined && c.price_ttc_estime !== null
                          ? formatCurrency(Number(c.price_ttc_estime))
                          : '—'}
                      </td>
                      <td className="py-1.5 pr-2 align-top text-center">
                        {c.in_stock
                          ? <span className="text-emerald-400">✓</span>
                          : <span className="text-gray-500">{c.stock_status || '—'}</span>}
                      </td>
                      <td className="py-1.5 pr-2 align-top text-center">
                        {c.model_exact ? <span className="text-emerald-400">✓</span> : <span className="text-gray-500">—</span>}
                      </td>
                      <td className="py-1.5 align-top text-right">
                        {!isCur && (
                          <button
                            type="button"
                            disabled={busyId === d.id || d.status !== 'pending'}
                            onClick={() => refreshPart(d.id, {
                              supplier: c.supplier,
                              supplier_id: c.supplier_id || undefined,
                              reference: c.reference || undefined,
                            })}
                            className="text-[11px] px-2.5 py-1 rounded-md bg-cyan-600/20 text-cyan-300 hover:bg-cyan-600/35 disabled:opacity-40"
                          >
                            {t('chatAiDevis.useBtn')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {missing.length > 0 && rows.length > 0 && (
          <p className="text-[10px] text-gray-500">{t('chatAiDevis.compareNoResultFor', { suppliers: missing.join(', ') })}</p>
        )}
      </div>
    );
  };

  const renderDevisCard = (d) => {
    const intent = d.intent || {};
    const payload = d.calcul_payload || {};
    const item = (payload.items || [])[0] || {};
    const snap = payload.supplier_snapshot || {};
    const achatVal = item.prix_achat ?? snap.price ?? 0;
    const moDefault = payload.main_oeuvre ?? 50;
    const achatMissing = Number(achatVal) <= 0;
    const quality = qualityLabel(intent.quality);
    const supplier = payload.fournisseur || snap.supplier || 'JensMobiles';
    const isPrimary = supplier === 'JensMobiles';
    const fam = familyBadge(intent);

    return (
      <div key={d.id} className="glass p-4 sm:p-5 space-y-4 border border-white/10 rounded-2xl overflow-hidden">
        {d.staff_note && d.status === 'pending' && (
          <div className="text-xs bg-blue-500/10 border border-blue-500/25 text-blue-200 rounded-lg px-3 py-2">
            {d.staff_note}
          </div>
        )}
        {achatMissing && d.status === 'pending' && (
          <div className="text-xs bg-red-500/15 border border-red-500/30 text-red-300 rounded-lg px-3 py-2">
            {d.needs_staff_pricing || d.source === 'cadbot_stub'
              ? t('chatAiDevis.cadbotEmptyCache')
              : t('chatAiDevis.purchaseMissingZero')}
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              <span className="text-xs font-mono text-amber-400 shrink-0">{d.ref}</span>
              {fam && <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${fam.cls}`}>{t(fam.labelKey)}</span>}
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-semibold shrink-0">{quality}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${supplierBadgeClass(supplier)}`}>
                {supplier}{isPrimary ? ` · ${t('chatAiDevis.supplierPrimary')}` : ` · ${t('chatAiDevis.supplierAlternate')}`}
              </span>
              {snapshotBadges(snap).map((b) => (
                <span key={b.labelKey} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${b.cls}`}>
                  {t(b.labelKey, b.vars)}
                </span>
              ))}
            </div>
            <h3 className="text-base sm:text-lg text-white font-semibold leading-snug">
              {intent.part || t('chatAiDevis.defaultRepair')} · {intent.model || intent.brand || '—'}
            </h3>
            {d.status === 'pending' ? (
              <div className="mt-2 rounded-xl border border-white/10 bg-white/[.03] p-2.5 space-y-1.5" data-testid={`identity-${d.id}`}>
                {(() => { const miss = identityMissing({ ...d, ...(editIdent[d.id] ? { client_name: editIdent[d.id].name ?? d.client_name, client_tel: editIdent[d.id].tel ?? d.client_tel, client_email: editIdent[d.id].email ?? (d.client_email || payload.client_email) } : {}) }); return miss.length ? (
                  <p className="text-[11px] text-amber-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {t('chatAiDevis.identityIncompleteInline', { fields: tMissing(miss) })}</p>
                ) : (
                  <p className="text-[11px] text-emerald-300/90 flex items-center gap-1"><Mail className="w-3 h-3" /> {t('chatAiDevis.willSendTo', { email: (editIdent[d.id]?.email ?? (d.client_email || payload.client_email)) || '—' })}</p>
                ); })()}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                  <label className="flex items-center gap-1 text-xs"><User className="w-3 h-3 text-gray-500 shrink-0" />
                    <input value={editIdent[d.id]?.name ?? (d.client_name || '')} onChange={(e) => setEditIdent((prev) => ({ ...prev, [d.id]: { ...(prev[d.id] || {}), name: e.target.value } }))} placeholder={t('chatAiDevis.phName')} className="input-dark text-xs py-1 w-full" data-testid={`identity-name-${d.id}`} />
                  </label>
                  <label className="flex items-center gap-1 text-xs"><Phone className="w-3 h-3 text-gray-500 shrink-0" />
                    <input value={editIdent[d.id]?.tel ?? (d.client_tel || '')} onChange={(e) => setEditIdent((prev) => ({ ...prev, [d.id]: { ...(prev[d.id] || {}), tel: e.target.value } }))} placeholder={t('chatAiDevis.phPhone')} inputMode="tel" className="input-dark text-xs py-1 w-full" data-testid={`identity-tel-${d.id}`} />
                  </label>
                  <label className="flex items-center gap-1 text-xs"><Mail className="w-3 h-3 text-gray-500 shrink-0" />
                    <input value={editIdent[d.id]?.email ?? (d.client_email || payload.client_email || '')} onChange={(e) => setEditIdent((prev) => ({ ...prev, [d.id]: { ...(prev[d.id] || {}), email: e.target.value } }))} placeholder={t('chatAiDevis.phEmail')} inputMode="email" className="input-dark text-xs py-1 w-full" data-testid={`identity-email-${d.id}`} />
                  </label>
                </div>
                {editIdent[d.id] && (
                  <button type="button" disabled={busyId === d.id} onClick={() => saveIdentity(d)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-cyan-600/30 text-cyan-200 text-xs hover:bg-cyan-600/50" data-testid={`identity-save-${d.id}`}>
                    <Save className="w-3 h-3" /> {t('chatAiDevis.saveIdentityBtn')}
                  </button>
                )}
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-400 mt-1">{d.client_name || t('chatAiDevis.defaultClient')} · {d.client_tel || '—'}</p>
                {(d.client_email || payload.client_email) && (
                  <p className="text-xs text-emerald-300/90 mt-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {t('chatAiDevis.willSendTo', { email: d.client_email || payload.client_email })}
                  </p>
                )}
              </>
            )}
          </div>
          <div className="sm:text-right shrink-0">
            <div className="text-2xl sm:text-3xl font-bold text-emerald-400 tabular-nums">{formatCurrency(Number(d.prix_final || 0))}</div>
            <div className="text-[10px] text-gray-500">{t('chatAiDevis.ttcCalcul')}</div>
          </div>
        </div>

        {!d.batch_id && (
          <p className="text-xs text-gray-400 italic border-l-2 border-amber-500/30 pl-3 leading-relaxed break-words">
            « {d.client_message} »
          </p>
        )}

        <div className="rounded-xl border border-white/15 bg-[#0a0a0f]/90 p-4 space-y-4">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-2">
              {t('chatAiDevis.selectedPart')}
            </p>
            <p
              className="text-sm sm:text-[15px] text-white leading-6 font-normal"
              style={{ overflowWrap: 'anywhere', wordBreak: 'normal' }}
            >
              {item.description || snap.part_name || '—'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 border-t border-white/10 text-sm">
            <div className="flex items-center gap-2 text-gray-300 min-w-0">
              <Store className="w-4 h-4 text-gray-500 shrink-0" />
              <span className="font-medium text-white">{supplier}</span>
              <span className="text-gray-500">·</span>
              <span className="font-mono text-xs text-gray-400 truncate max-w-[140px]">
                {item.reference || payload.ref_piece || snap.reference || '—'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <div className="space-y-2">
              <label htmlFor={`achat-${d.id}`} className="block text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
                {t('chatAiDevis.purchasePriceHt')}
              </label>
              <DevisAmountInput
                id={`achat-${d.id}`}
                value={editAchat[d.id] ?? achatVal}
                onChange={(e) => setEditAchat((prev) => ({ ...prev, [d.id]: e.target.value }))}
                disabled={busyId === d.id}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor={`mo-${d.id}`} className="block text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
                {t('chatAiDevis.labor')}
              </label>
              <DevisAmountInput
                id={`mo-${d.id}`}
                step="1"
                value={editMo[d.id] ?? moDefault}
                onChange={(e) => setEditMo((prev) => ({ ...prev, [d.id]: e.target.value }))}
                disabled={busyId === d.id}
              />
              <button
                type="button"
                disabled={busyId === d.id}
                onClick={() => patchMo(d.id)}
                className="w-full sm:w-auto text-xs px-3 py-2 rounded-lg bg-cyan-600/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-600/35 disabled:opacity-40 font-medium"
              >
                {t('chatAiDevis.recalcTotal')}
              </button>
            </div>
          </div>
        </div>

        {d.status === 'pending' && (
          <>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busyId === d.id}
                onClick={() => loadCompare(d.id)}
                className="flex items-center gap-1.5 text-xs text-cyan-300 hover:text-white px-2.5 py-1.5 rounded-lg border border-cyan-500/25 bg-cyan-500/5 hover:bg-cyan-500/15 disabled:opacity-40"
                data-testid={`compare-btn-${d.id}`}
              >
                <Layers className="w-3.5 h-3.5" />
                {expandedCompare[d.id] ? t('chatAiDevis.hideCompare') : t('chatAiDevis.compareBtn')}
              </button>
              <button
                type="button"
                disabled={busyId === d.id}
                onClick={() => loadAlternatives(d.id)}
                className="flex items-center gap-1.5 text-xs text-gray-300 hover:text-white px-2 py-1.5 rounded-lg hover:bg-white/5"
              >
                {expandedAlts[d.id] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {expandedAlts[d.id] ? t('chatAiDevis.hideAlternatives') : t('chatAiDevis.showAlternatives')}
              </button>
            </div>
            {renderCompare(d)}
            {renderAlternatives(d)}
          </>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
          {d.conversation_id && d.status === 'pending' && (
            <button
              type="button"
              onClick={() => openBotChat({
                conversationId: d.conversation_id,
                clientName: d.client_name,
                clientMessage: d.client_message,
                clientTel: d.client_tel,
                clientEmail: d.client_email || (d.calcul_payload || {}).client_email || '',
              })}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-500/10 text-purple-300 text-xs hover:bg-purple-500/20"
            >
              <Bot className="w-3.5 h-3.5" /> {t('chatAiDevis.botStaff')}
            </button>
          )}
          {d.status === 'pending' && (
            <>
              <button type="button" disabled={busyId === d.id} onClick={() => refreshPart(d.id)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-500/15 text-blue-300 text-xs hover:bg-blue-500/25">
                <RefreshCw className="w-3.5 h-3.5" /> {t('chatAiDevis.bestPartAuto')}
              </button>
              <button type="button" disabled={busyId === d.id} onClick={() => previewPdf(d.id)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-gray-300 text-xs hover:bg-white/10">
                <Eye className="w-3.5 h-3.5" /> {t('chatAiDevis.pdf')}
              </button>
              <button type="button" disabled={busyId === d.id} onClick={() => approve(d.id)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600/60 text-white text-xs hover:bg-emerald-600">
                <CheckCircle2 className="w-3.5 h-3.5" /> {t('chatAiDevis.approveSingle')}
              </button>
              <button type="button" disabled={busyId === d.id} onClick={() => reject(d.id)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/15 text-red-400 text-xs hover:bg-red-500/25">
                <XCircle className="w-3.5 h-3.5" /> {t('chatAiDevis.reject')}
              </button>
            </>
          )}
          <button type="button" disabled={busyId === d.id} onClick={() => printDevisTicket(d)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/15 text-amber-300 text-xs hover:bg-amber-500/25">
            <Printer className="w-3.5 h-3.5" /> {t('chatAiDevis.print')}
          </button>
          {d.status !== 'pending' && (
            <button type="button" disabled={busyId === d.id} onClick={() => previewPdf(d.id)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-gray-300 text-xs hover:bg-white/10">
              <Eye className="w-3.5 h-3.5" /> {t('chatAiDevis.pdf')}
            </button>
          )}
          {canManageLots && (
            <button
              type="button"
              disabled={busyId === d.id}
              onClick={() => deleteDevis(d.id, d.ref)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-900/30 text-red-300 text-xs hover:bg-red-900/50 border border-red-500/20"
              title={t('chatAiDevis.dirAdminTitle')}
            >
              <Trash2 className="w-3.5 h-3.5" /> {t('chatAiDevis.delete')}
            </button>
          )}
        </div>

        {d.status === 'approved' && (
          <p className="text-xs text-emerald-400">
            {t('chatAiDevis.approvedShort')} · {d.calcul_ref_doc} · {formatCurrency(Number(d.prix_final))}
          </p>
        )}
        {d.status === 'rejected' && (
          <p className="text-xs text-red-400/80">{t('chatAiDevis.rejectedShort')}{d.staff_note ? ` · ${d.staff_note}` : ''}</p>
        )}
      </div>
    );
  };

  const renderBatch = ([batchId, items]) => {
    const first = items[0] || {};
    const intent = first.intent || {};
    const pendingCount = items.filter((d) => d.status === 'pending').length;
    const allPending = pendingCount === items.length && pendingCount > 0;

    return (
      <div key={batchId} className="space-y-3 border border-amber-500/20 rounded-2xl p-4 bg-amber-500/5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-amber-400 text-xs font-semibold uppercase tracking-wide">
              <Layers className="w-4 h-4" />
              {t('chatAiDevis.batchLabel', { count: items.length })} · {intent.part || t('chatAiDevis.defaultRepair')} {intent.model || ''}
            </div>
            <p className="text-xs text-gray-400 italic mt-1">« {first.client_message} »</p>
            <p className="text-[10px] text-gray-500 mt-1">{first.client_name} · {first.client_tel || '—'}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={`/chat?conversation=${first.conversation_id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-gray-300 text-xs hover:bg-white/10">
              <MessageSquare className="w-3.5 h-3.5" /> {t('chatAiDevis.clientChat')}
            </a>
            <button
              type="button"
              onClick={() => openBotChat({
                conversationId: first.conversation_id,
                clientName: first.client_name,
                clientMessage: first.client_message,
                clientTel: first.client_tel,
                clientEmail: first.client_email || (first.calcul_payload || {}).client_email || '',
              })}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs ${
                botChat?.conversationId === first.conversation_id
                  ? 'bg-purple-500/25 text-purple-200 border border-purple-500/40'
                  : 'bg-purple-500/10 text-purple-300 hover:bg-purple-500/20'
              }`}
            >
              <Bot className="w-3.5 h-3.5" /> {t('chatAiDevis.botStaff')}
            </button>
            {allPending && (
              <button
                type="button"
                disabled={busyId === batchId}
                onClick={() => approveBatch(batchId, items.length)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-500"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> {t('chatAiDevis.approveAllSend', { count: items.length })}
              </button>
            )}
            {canManageLots && (
              <button
                type="button"
                disabled={busyId === batchId}
                onClick={() => deleteBatch(batchId, items.length)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-900/30 text-red-300 text-xs hover:bg-red-900/50 border border-red-500/20"
                title={t('chatAiDevis.dirAdminTitle')}
              >
                <Trash2 className="w-3.5 h-3.5" /> {t('chatAiDevis.deleteBatch')}
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {items.map(renderDevisCard)}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6 fade-in" data-testid="chat-ai-devis-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{t('chatAiDevis.pageTitle')}</h2>
              <p className="text-sm text-gray-400">{t('chatAiDevis.pageSubtitle')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {['pending', 'approved', 'rejected', 'all'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                  filter === s ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-white/5 text-gray-400'
                }`}
              >
                {s === 'pending' ? t('chatAiDevis.filterPending') : s === 'approved' ? t('chatAiDevis.filterApproved') : s === 'rejected' ? t('chatAiDevis.filterRejected') : t('chatAiDevis.filterAll')}
              </button>
            ))}
            <button type="button" onClick={load} className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4 text-sm text-gray-300 space-y-2">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-cyan-200">{t('chatAiDevis.infoTitle')}</p>
              <ul className="mt-1 text-xs text-gray-400 space-y-1 list-disc list-inside">
                <li><strong className="text-gray-300">JensMobiles</strong> {t('chatAiDevis.infoJensPrimary')}</li>
                <li><strong className="text-gray-300">Mobilax</strong> {t('chatAiDevis.infoAnd')} <strong className="text-gray-300">LCD-Phone</strong> {t('chatAiDevis.infoMobilaxLcd')}</li>
                <li>{t('chatAiDevis.infoEachRange')}</li>
                <li>{t('chatAiDevis.infoCompareExplain')}</li>
                <li>{t('chatAiDevis.infoBadgesLabel')} <span className="text-orange-300">{t('chatAiDevis.badgeFallback')}</span>, <span className="text-sky-300">{t('chatAiDevis.badgeModelScoreShort')}</span>, <span className="text-red-300">{t('chatAiDevis.badgeNeedsReview')}</span> {t('chatAiDevis.infoBadgesCheck')}</li>
                <li><strong className="text-gray-300">{t('chatAiDevis.botStaff')}</strong> {t('chatAiDevis.infoBotStaffExplain')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-col xl:flex-row gap-6 items-start">
          <div className="flex-1 min-w-0 space-y-6 w-full">
            {loading ? (
              <p className="text-gray-500 text-sm">{t('chatAiDevis.loading')}</p>
            ) : devis.length === 0 ? (
              <div className="glass p-8 text-center text-gray-500">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>{filter === 'pending' ? t('chatAiDevis.noQuotesPending') : t('chatAiDevis.noQuotes')}</p>
                <p className="text-xs text-purple-400/80 mt-3">
                  {t('chatAiDevis.emptyHint')}
                </p>
              </div>
            ) : (
              <>
                {batches.map(renderBatch)}
                {singles.map(renderDevisCard)}
              </>
            )}
          </div>

          <div className="hidden xl:block w-full xl:w-[360px] shrink-0 space-y-2">
            {botMode === 'client' && botChat && (
              <button
                type="button"
                onClick={resetBotWorkspace}
                className="text-[10px] text-purple-400 hover:text-purple-300 underline"
              >
                {t('chatAiDevis.backToStaff')}
              </button>
            )}
            <AutoDevisStaffBotChat
              api={api}
              mode={botMode === 'client' && botChat ? 'client' : 'workspace'}
              conversationId={botChat?.conversationId}
              clientName={botChat?.clientName}
              clientMessage={botChat?.clientMessage}
              clientTel={botChat?.clientTel}
              clientEmail={botChat?.clientEmail}
              onDevisCreated={() => load()}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowMobileBot(true)}
          className="xl:hidden fixed bottom-20 right-4 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-purple-600 text-white shadow-lg shadow-purple-900/40 text-sm font-semibold"
          data-testid="autodevis-bot-fab"
        >
          <Bot className="w-5 h-5" /> {t('chatAiDevis.botAutoDevis')}
        </button>

        {showMobileBot && (
          <div className="xl:hidden fixed inset-0 z-50 flex flex-col bg-black/70 p-3">
            <div className="flex-1 min-h-0 max-w-lg mx-auto w-full flex flex-col gap-2">
              {botMode === 'client' && botChat && (
                <button type="button" onClick={resetBotWorkspace} className="text-xs text-purple-300 text-left">
                  {t('chatAiDevis.staffSpace')}
                </button>
              )}
              <AutoDevisStaffBotChat
                api={api}
                mode={botMode === 'client' && botChat ? 'client' : 'workspace'}
                conversationId={botChat?.conversationId}
                clientName={botChat?.clientName}
                clientMessage={botChat?.clientMessage}
                clientTel={botChat?.clientTel}
                clientEmail={botChat?.clientEmail}
                compact
                onClose={() => setShowMobileBot(false)}
                onDevisCreated={() => { load(); setShowMobileBot(false); }}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ChatAiDevisPage;
